import { useState } from "preact/compat";
import type { ImageZoomOffset } from "@brickme/project-core/src/model/picture.ts";
import type {
	GenericSize,
	PixelPosition,
} from "@brickme/project-core/src/model/geom.ts";
import {
	arePositionsClose,
	addPositions,
	subtractPositions,
} from "@brickme/project-core/src";
import roundAndConstrainImageZoomOffset from "@brickme/project-core/src/utils/round-and-constrain-image-zoom-offset.ts";
import { useRenderMode } from "../picture-render/render-mode-context.tsx";
import { usePicture } from "./picture-edit-context.tsx";
import type { MouseOrTouchCanvasEvent } from "../utils/canvas.ts";
import {
	getCanvasPixelPosition,
	canvasPositionToBrick,
} from "../utils/canvas.ts";

const sourceRenderModeId = "zoom-drag";

type ZoomDragStart = {
	readonly mousePosition: PixelPosition;
	readonly imageZoomOffset: ImageZoomOffset;
};

type ImageZoomDraggingOptions = {
	readonly sourceImageDimensions: GenericSize<number>;
};

function useImageZoomDragging({
	sourceImageDimensions,
}: ImageZoomDraggingOptions) {
	const { picture, patchPictureDebounced } = usePicture();
	const { imageZoomOffset, numberOfBasePlates, basePlateSize } = picture;
	const { requestRenderMode, withdrawRenderModeRequest } = useRenderMode();

	const [zoomDraggingStart, setZoomDraggingStart] = useState<
		ZoomDragStart | undefined
	>(undefined);
	// TODO: Don't request low quality render or show hand drag icon if no space possible.
	// i.e. aspect ratio of source must equal aspect ratio number of base plates and zoom == 1
	function onZoomDragStart(e: MouseOrTouchCanvasEvent) {
		const mousePosition = getCanvasPixelPosition(e);
		if (!mousePosition) {
			return;
		}

		requestRenderMode(sourceRenderModeId, "source");
		setZoomDraggingStart({
			mousePosition,
			imageZoomOffset,
		});
	}

	function onZoomDragMove(e: MouseOrTouchCanvasEvent) {
		if (!zoomDraggingStart) {
			console.error("Invalid state - moving while start not called");
			return;
		}

		const mousePosition = getCanvasPixelPosition(e);
		if (!mousePosition) {
			return;
		}

		const mouseOffset = subtractPositions(
			mousePosition,
			zoomDraggingStart.mousePosition,
		);

		const brickOffset = canvasPositionToBrick(e.currentTarget, mouseOffset, {
			width: numberOfBasePlates.width * basePlateSize,
			height: numberOfBasePlates.height * basePlateSize,
		});

		const newOffset = roundAndConstrainImageZoomOffset(
			sourceImageDimensions,
			picture,
			addPositions(zoomDraggingStart.imageZoomOffset, brickOffset),
		);
		// redux action is very heavy (or maybe it's just the listening consequences)
		// worth preventing it
		if (arePositionsClose(newOffset, imageZoomOffset, 0.5)) {
			return;
		}

		patchPictureDebounced({
			imageZoomOffset: newOffset,
		});
	}
	function onZoomDragEnd() {
		withdrawRenderModeRequest(sourceRenderModeId);
		setZoomDraggingStart(undefined);
	}

	// const mouseHandlers = (() => {
	// 	if (isPinching || isDisplayZoomPinching) {
	// 		return { state: "pinching" };
	// 	}

	// 	if (isImageZoomActive) {
	// 		if (imageDragging.isDragging) {
	// 			return {
	// 				state: "zoom-dragging",
	// 				onMouseMove: imageDragging.onDragMove,
	// 				onTouchMove: imageDragging.onDragMove,
	// 				onMouseUp: imageDragging.onDragEnd,
	// 				onMouseLeave: imageDragging.onDragEnd,
	// 				onTouchEnd: imageDragging.onDragEnd,
	// 				onTouchCancel: imageDragging.onDragEnd,
	// 			};
	// 		}
	// 		return {
	// 			state: "zoom-drag",
	// 			onMouseDown: imageDragging.onDragStart,
	// 			onTouchStart: imageDragging.onDragStart,
	// 		};
	// 	}

	// 	return undefined;
	// })();

	const isDragging = !!zoomDraggingStart;
	return {
		isDragging,
		imageZoomCanvasHandlers: isDragging
			? {
					onMouseMove: onZoomDragMove,
					onTouchMove: onZoomDragMove,
					onMouseUp: onZoomDragEnd,
					onMouseLeave: onZoomDragEnd,
					onTouchEnd: onZoomDragEnd,
					onTouchCancel: onZoomDragEnd,
				}
			: {
					onMouseDown: onZoomDragStart,
					onTouchStart: onZoomDragStart,
				},
	};
}

export default useImageZoomDragging;
